import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getArticlesByPersonneId } from '../../../firebaseServices/firebaseServices'; // Importez votre fonction ici
import { Text } from '../../atoms/Text';
import { marked } from 'marked';

// STYLES
import {
  colors,
  fontFamily,
  fontSize,
} from '../../../Design_System/DesignSystem';

// Type pour les objets de itemsPensee
interface Pensee {
  id: string;
  name: string;
  date: string; // Vous pouvez également utiliser Date si vous manipulez les dates
}

// Type pour les objets de itemsLogs
interface Log {
  id: string;
  date: string; // Même remarque que ci-dessus
}

// Type pour les objets de itemsGens
interface Personne {
  id: string;
  name: string;
  onContact: boolean;
}

// Définition de l'interface pour les éléments
type Item = Pensee | Log | Personne;

interface BlogInterface {
  blog: Item;
  setSelectedItem: React.Dispatch<React.SetStateAction<number>>;
}

const Blog = ({ blog, setSelectedItem }: BlogInterface) => {
  const [articles, setArticles] = useState<any[]>([]); // État pour stocker les articles
  const [loading, setLoading] = useState<boolean>(true); // État de chargement
  const [markdownContents, setMarkdownContents] = useState<string[]>([]); // État pour stocker les contenus Markdown convertis

  // Fonction utilitaire pour vérifier si blog est une Personne
  const isPersonne = (blog: Item): blog is Personne => {
    return 'name' in blog && 'onContact' in blog;
  };

  // Utilisation de useEffect pour récupérer les articles si blog est une Personne
  useEffect(() => {
    if (isPersonne(blog)) {
      const fetchArticles = async () => {
        setLoading(true);
        const fetchedArticles = await getArticlesByPersonneId(blog.id);
        setArticles(fetchedArticles);
        setLoading(false);
      };
      fetchArticles();
    }
  }, [blog]);

  // Utilisation de useEffect pour convertir le Markdown en HTML
  useEffect(() => {
    const convertMarkdownToHTML = async () => {
      const convertedContents = await Promise.all(
        articles.map((article) => marked(article.markdownContent))
      );
      setMarkdownContents(convertedContents);
    };

    if (articles.length > 0) {
      convertMarkdownToHTML();
    }
  }, [articles]);

  // Vérifiez le type de `blog` et affichez les propriétés appropriées
  const renderContent = () => {
    const titleContent =
      'name' in blog ? blog.name : 'date' in blog ? blog.date : null;
    const userEmail = localStorage.getItem(
      'a2b9fd36-29a5-41c9-8de9-49ce7e7c9c3c'
    ) as string;

    if (!titleContent) return null; // Return early if neither 'name' nor 'date' exists

    return (
      <BlogContainer>
        <TitleContainer>
          <Text
            color={colors.tan}
            fontSize={fontSize.title}
            fontFamily={fontFamily.playfair}
            justify
            bold={false}
          >
            {titleContent}
          </Text>
          <Title onClick={() => setSelectedItem(7)}>
            <Text
              color={colors.tan}
              fontSize={fontSize.small}
              fontFamily={fontFamily.tinos}
              bold={true}
              justify={false}
            >
              Retour
            </Text>
          </Title>
        </TitleContainer>
        {userEmail === 'tristan.lemire.sio@gmail.com' && (
          <AddPost onClick={() => setSelectedItem(10)}>
            <Text
              color={colors.tan}
              fontSize={fontSize.small}
              fontFamily={fontFamily.tinos}
              bold={true}
              justify={false}
            >
              Ajouter un post
            </Text>
          </AddPost>
        )}

        {/* Affichage des articles si blog est une Personne */}
        {loading ? (
          <p>Chargement des articles...</p>
        ) : articles.length > 0 ? (
          articles
            .sort((a, b) => b.createdAt.seconds - a.createdAt.seconds) // Trier les articles par createdAt décroissant
            .map((article, index) => (
              <Article key={article.id}>
                <h3>{article.title}</h3>

                <small>
                  Créé le :{' '}
                  {new Date(
                    article.createdAt.seconds * 1000
                  ).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}{' '}
                  à{' '}
                  {new Date(
                    article.createdAt.seconds * 1000
                  ).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </small>
                <div
                  dangerouslySetInnerHTML={{
                    __html: markdownContents[index], // Utilisez le contenu converti correspondant
                  }}
                />
              </Article>
            ))
        ) : (
          <p>Aucun article trouvé pour cette personne.</p>
        )}
      </BlogContainer>
    );
  };

  return (
    <HomeContainer>
      <Container>
        {renderContent()}
        <br />
      </Container>
    </HomeContainer>
  );
};

const BlogContainer = styled.div`
  color: ${colors.tan};
`;

const AddPost = styled.div`
  cursor: pointer;
`;

const HomeContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Pour éviter le débordement */
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  margin: 20px;
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  overflow: hidden; /* Pour éviter le débordement */
  justify-content: space-between;
  align-items: flex-end;
`;

const Title = styled.div`
  cursor: pointer;
`;

const Article = styled.div`
  margin-top: 20px;
  border: 1px solid ${colors.tan};
  padding: 10px;
  border-radius: 4px;

  h3 {
    margin: 0;
    color: ${colors.tan};
  }

  p {
    color: ${colors.tan};
  }

  small {
    display: block;
    margin-top: 10px;
    color: ${colors.tan};
  }
`;

export default Blog;
